import { isDefiniteShopRejection } from "./shopMutationOutcome";
import { useEffect, useRef, useState } from "react";
import type { User } from "firebase/auth";
import { ShopApiError, shopAdminApi, type ProcessingBatchDraft, type ProcessingBatchResult, type ProcessingBatchSnapshot } from "./shopAdminApi";

export function useProcessingBatchWriter(user: User | null | undefined) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [version, setVersion] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const epoch = useRef(0);
  const inFlight = useRef(false);
  const attempted = useRef<{ documentId?: string; actorUid: string; uncertain?: boolean; body: { draft: ProcessingBatchDraft; reason: string; expectedDocumentVersion: string }; key: string } | null>(null);
  useEffect(() => () => { epoch.current++; }, []);
  const reset = () => {
    if (inFlight.current || attempted.current) return;
    epoch.current++; setLoading(false); setSaving(false); setPending(false);
    setConflict(false); setVersion(""); setReason(""); setError("");
  };
  const load = async (documentId: string, hydrate: (snapshot: ProcessingBatchSnapshot) => void) => {
    if (inFlight.current || attempted.current) return;
    reset(); setLoading(true);
    const current = epoch.current;
    try {
      if (!user) throw new ShopApiError(401, "unauthenticated");
      const snapshot = await shopAdminApi.processingBatch(user, documentId);
      if (current !== epoch.current) return;
      hydrate(snapshot); setVersion(snapshot.expectedDocumentVersion);
    } catch {
      if (current === epoch.current) setError("Aktuella batchuppgifter kunde inte hämtas. Kontrollera API-anslutning och behörighet.");
    } finally { if (current === epoch.current) setLoading(false); }
  };
  const save = async (documentId: string | undefined, draft: ProcessingBatchDraft): Promise<ProcessingBatchResult | null> => {
    if (!user || inFlight.current || loading || conflict || (!attempted.current && (!reason.trim() || reason.trim().length > 500 || (documentId && !version)))) return null;
    inFlight.current = true; setSaving(true); setPending(true); setError("");
    const current = epoch.current;
    if (!attempted.current) attempted.current = { documentId, actorUid: user.uid, body: { draft: structuredClone(draft), reason: reason.trim(), expectedDocumentVersion: version }, key: crypto.randomUUID() };
    const frozen = attempted.current;
    try {
      if (frozen.actorUid !== user.uid) { setError("Logga in som användaren som påbörjade sparandet för att bekräfta samma åtgärd."); return null; }
      const result = frozen.documentId ? await shopAdminApi.updateProcessingBatch(user, frozen.documentId, frozen.body, frozen.key) :
        await shopAdminApi.createProcessingBatch(user, { draft: frozen.body.draft, reason: frozen.body.reason }, frozen.key);
      if (current !== epoch.current) return null;
      attempted.current = null; setPending(false); return result;
    } catch (caught) {
      if (current !== epoch.current) return null;
      if (isDefiniteShopRejection(caught, attempted.current?.uncertain)) {
        attempted.current = null; setPending(false); setConflict(true);
        setError(caught instanceof ShopApiError && (caught.status === 401 || caught.status === 403) ? "Du saknar aktuell behörighet att spara batchen." :
          "Uppgifterna eller kopplingarna har ändrats, eller ändringen är spärrad. Använda batchers råvara, process och spårbarhet måste behållas. Granska aktuella uppgifter och skapa en ny batch vid behov.");
      } else { if (attempted.current) attempted.current.uncertain = true; setError("Sparandet kunde inte bekräftas. Försök igen med samma uppgifter; servern upprepar inte en genomförd åtgärd."); }
      return null;
    } finally { inFlight.current = false; if (current === epoch.current) setSaving(false); }
  };
  const retry = () => {
    const frozen = attempted.current;
    return frozen ? save(frozen.documentId, frozen.body.draft) : Promise.resolve(null);
  };
  return { loading, saving, pending, conflict, version, reason, setReason, error, reset, load, save, retry };
}
